/**
 * The letter shown before it is sent.
 *
 * An answer to a signed in player is a notification and can be taken back by
 * writing a second one. An answer to a visitor is an e-mail, and an e-mail is
 * gone the moment it leaves - so the form asks the server what it would send,
 * shows exactly that, and only then submits.
 *
 * The preview is composed by the endpoint rather than assembled here on
 * purpose: a preview built in the browser is a second guess at the wording,
 * and the one thing a preview may never be is nearly right.
 *
 * Without a working <dialog> the button is simply not wired: the form still
 * submits the ordinary way, which sends the same letter without showing it.
 */

import { dialogsWork } from "./modal";
import { query, queryAll } from "../core/dom";
import { text } from "../core/i18n";

/** The letter as the server composed it. */
export interface LetterPreview {
    readonly to: string;
    readonly from: string;
    readonly subject: string;
    readonly body: string;
    readonly ready: boolean;
}

/** Return whether `value` carries the four fields of a letter. */
export function isLetterPreview(value: unknown): value is LetterPreview {
    if (typeof value !== "object" || value === null) {
        return false;
    }

    const letter = value as Partial<LetterPreview>;

    return typeof letter.subject === "string" && typeof letter.body === "string" && typeof letter.to === "string";
}

/** Write a letter into the dialog. */
export function fillPreview(dialog: HTMLElement, letter: LetterPreview): void {
    const fields: [string, string][] = [
        ["[data-letter-from]", letter.from],
        ["[data-letter-to]", letter.to],
        ["[data-letter-subject]", letter.subject],
        ["[data-letter-body]", letter.body],
    ];

    for (const [selector, value] of fields) {
        const slot = query<HTMLElement>(selector, dialog);
        if (slot) {
            slot.textContent = value;
        }
    }
}

/** Show a refusal inside the dialog, or clear the one which is shown. */
export function showError(dialog: HTMLElement, message: string): void {
    const box = query<HTMLElement>("[data-ticket-error]", dialog);
    const slot = query<HTMLElement>("[data-ticket-error-text]", dialog);

    if (!box || !slot) {
        return;
    }

    slot.textContent = message;
    box.hidden = message.length === 0;
}

/** Ask the server what the answer typed into `form` would be sent as. */
export async function requestPreview(form: HTMLFormElement): Promise<LetterPreview> {
    const url = form.dataset["previewUrl"] ?? "";
    const response = await fetch(url, {
        method: "POST",
        body: new FormData(form),
        credentials: "same-origin",
        headers: { Accept: "application/json" },
    });

    const payload: unknown = await response.json().catch(() => null);

    if (!response.ok) {
        const detail =
            typeof payload === "object" &&
            payload !== null &&
            typeof (payload as { detail?: string }).detail === "string"
                ? (payload as { detail: string }).detail
                : text("js.ticket.preview_failed", "Náhľad sa nepodarilo načítať.");

        throw new Error(detail);
    }

    if (!isLetterPreview(payload)) {
        throw new Error(text("js.ticket.preview_failed", "Náhľad sa nepodarilo načítať."));
    }

    return payload;
}

/**
 * Keep a button from offering to send nothing.
 *
 * The box decides: while it is empty there is nothing to preview and nothing
 * to send, and a button which is pressable then only produces a refusal from
 * the server. A button marked as blocked - the answer would have to leave as
 * an e-mail the portal cannot post - is never enabled at all.
 */
export function guardComposer(box: HTMLTextAreaElement, buttons: readonly HTMLButtonElement[]): void {
    const update = (): void => {
        const written = box.value.trim().length > 0;

        for (const button of buttons) {
            button.disabled = !written || button.hasAttribute("data-send-blocked");
        }
    };

    box.addEventListener("input", update);
    update();
}

/** Wire the two boxes of the ticket screen and the preview dialog. */
export function initTicketReply(): void {
    const form = query<HTMLFormElement>("[data-ticket-reply]");
    const reply = query<HTMLTextAreaElement>("[data-ticket-reply-body]");
    const note = query<HTMLTextAreaElement>("[data-note-body]");

    if (reply) {
        guardComposer(reply, [
            ...queryAll<HTMLButtonElement>("[data-ticket-send]"),
            ...queryAll<HTMLButtonElement>("[data-ticket-preview]"),
        ]);
    }

    if (note) {
        guardComposer(note, queryAll<HTMLButtonElement>("[data-note-submit]"));
    }

    const dialog = query<HTMLDialogElement>("[data-ticket-dialog]");

    if (!dialog || !form || !dialogsWork()) {
        return;
    }

    const confirm = query<HTMLButtonElement>("[data-ticket-confirm]", dialog);
    const cancel = query<HTMLButtonElement>("[data-ticket-cancel]", dialog);

    for (const opener of queryAll<HTMLElement>("[data-ticket-preview]")) {
        opener.addEventListener("click", () => {
            void (async () => {
                showError(dialog, "");
                fillPreview(dialog, {
                    to: "",
                    from: "",
                    subject: "",
                    body: "",
                    ready: true,
                });

                try {
                    const letter = await requestPreview(form);

                    fillPreview(dialog, letter);

                    if (!letter.ready) {
                        showError(dialog, text("js.ticket.no_mail", "Odosielanie e-mailov nie je nastavené."));
                    }
                } catch (error) {
                    showError(dialog, error instanceof Error ? error.message : String(error));
                }

                if (!dialog.open) {
                    dialog.showModal();
                }
            })();
        });
    }

    cancel?.addEventListener("click", () => dialog.close());
    confirm?.addEventListener("click", () => {
        dialog.close();
        form.submit();
    });
}
